import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Divider,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  updateProfile,
  getProfile,
  updatePassword,
  uploadProfileImage,
  deleteProfileImage,
} from "../../redux/slices/authSlice";
import {
  getOwnerPaymentSettings,
  updatePaymentSettings,
  updateLocalSettings,
  selectPaymentSettings,
  selectSettingsLoading,
  selectOperationLoading,
  selectOperationSuccess,
  clearOperationState,
} from "../../redux/slices/paymentsSlice";
import { toast } from "react-toastify";
import { Autocomplete } from "@mui/material";
import { State, City } from "india-state-city";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutline from "@mui/icons-material/PersonOutline";
import Business from "@mui/icons-material/Business";
import LockReset from "@mui/icons-material/LockReset";
import PaymentIcon from "@mui/icons-material/Payment";
import ProfileImageUpload from "../../components/ProfileImageUpload";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";

export default function Profile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { user, operations } = useSelector((state) => state.user);
  const paymentSettings = useSelector(selectPaymentSettings);
  const paymentSettingsLoading = useSelector(selectSettingsLoading);
  const paymentOperationLoading = useSelector(selectOperationLoading);
  const paymentOperationSuccess = useSelector(selectOperationSuccess);

  const role = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase()
    : "Owner";

  const isOwner = user?.role === "OWNER";

  const profileUpdate = operations?.profileUpdate || {
    loading: false,
    success: false,
    error: null,
  };
  const passwordUpdate = operations?.passwordUpdate || {
    loading: false,
    success: false,
    error: null,
  };
  const imageUpload = operations?.imageUpload || {
    loading: false,
    success: false,
    error: null,
  };
  const imageDelete = operations?.imageDelete || {
    loading: false,
    success: false,
    error: null,
  };

  const [tabIndex, setTabIndex] = useState(0);

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [profileImage, setProfileImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [paymentFormData, setPaymentFormData] = useState({
    cashEnabled: true,
    upiEnabled: true,
    upiId: "",
    upiQrCode: "",
    phonePeEnabled: true,
    phonePeNumber: "",
  });

  const [localProfile, setLocalProfile] = useState({
    restaurantName: "",
    ownerName: "",
    mobile: "",
    whatsappNumber: "",
    email: "",
    restaurantAddress: "",
    state: "",
    city: "",
    delivery: false,
    bankName: "",
    accountName: "",
    accountNumber: "",
    upiId: "",
    ifscCode: "",
    branchName: "",
    branchAddress: "",
    openingTime: "09:00",
    closingTime: "21:00",
    deliveryTimeMin: 30,
    deliveryTimeMax: 45,
    dineInAvailable: false,
    dineInTotalTables: 0,
    dineInAvailableTables: 0,
    isWhatsAppNotifications: false,
    orderTypeDineIn: true,
    orderTypeTakeaway: false,
    orderTypeDelivery: false,
    // ⭐ Delivery Management & Rules Fields
    deliveryCharge: 0,
    freeDeliveryThreshold: 0,
    minOrderAmountEnabled: false,
    minOrderAmount: 0,
  });

  useEffect(() => {
    if (profileUpdate.error) {
      toast.error(profileUpdate.error);
      dispatch({ type: "user/resetProfileUpdate" });
    }
    if (passwordUpdate.error) {
      toast.error(passwordUpdate.error);
      dispatch({ type: "user/resetPasswordUpdate" });
    }
    if (imageUpload.error) {
      toast.error(imageUpload.error);
      dispatch({ type: "user/resetImageUpload" });
    }
    if (imageDelete.error) {
      toast.error(imageDelete.error);
      dispatch({ type: "user/resetImageDelete" });
    }
  }, [
    profileUpdate.error,
    passwordUpdate.error,
    imageUpload.error,
    imageDelete.error,
    dispatch,
  ]);

  useEffect(() => {
    if (user?.role === "OWNER") {
      dispatch(getOwnerPaymentSettings());
    }
  }, [dispatch, user?.role]);

  useEffect(() => {
    if (paymentSettings) {
      setPaymentFormData(paymentSettings);
    }
  }, [paymentSettings]);

  useEffect(() => {
    if (paymentOperationSuccess) {
      toast.success("Payment settings updated successfully!");
      setTimeout(() => {
        dispatch(clearOperationState());
      }, 3000);
    }
  }, [paymentOperationSuccess, dispatch]);

  useEffect(() => {
    let mounted = true;

    if (passwordUpdate.success) {
      toast.success("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        if (mounted) dispatch({ type: "user/resetPasswordUpdate" });
      }, 2000);
    }

    if (imageUpload.success) {
      toast.success("Profile image uploaded successfully!");
      setTimeout(() => {
        if (mounted) {
          dispatch(getProfile());
          setImageFile(null);
          dispatch({ type: "user/resetImageUpload" });
        }
      }, 2000);
    }

    if (imageDelete.success) {
      toast.success("Profile image removed successfully!");
      setProfileImage(null);
      setTimeout(() => {
        if (mounted) {
          dispatch(getProfile());
          dispatch({ type: "user/resetImageDelete" });
        }
      }, 2000);
    }

    return () => {
      mounted = false;
    };
  }, [
    passwordUpdate.success,
    imageUpload.success,
    imageDelete.success,
    dispatch,
  ]);

  // Load user data into localProfile
  useEffect(() => {
    if (user) {
      const whatsAppValue = user.isWhatsAppNotifications;
      let dineInAvailable = false;
      let dineInTotalTables = 0;
      let dineInAvailableTables = 0;
      if (user.dineIn) {
        dineInAvailable = user.dineIn.isAvailable === true;
        dineInTotalTables = user.dineIn.totalTables || 0;
        dineInAvailableTables = user.dineIn.availableTables || 0;
      }

      const orderTypeDineIn =
        user.orderType?.DINE_IN !== undefined ? user.orderType.DINE_IN : true;
      const orderTypeTakeaway =
        user.orderType?.TAKEAWAY !== undefined
          ? user.orderType.TAKEAWAY
          : false;
      const orderTypeDelivery =
        user.orderType?.DELIVERY !== undefined
          ? user.orderType.DELIVERY
          : false;

      // Delivery Settings Load
      const dSettings = user.deliverySettings || {};

      setLocalProfile({
        restaurantName: user.restaurantName || "",
        ownerName: user.ownerName || "",
        mobile: user.mobile || "",
        whatsappNumber: user.whatsappNumber || "",
        email: user.email || "",
        restaurantAddress: user.restaurantAddress || "",
        state: user.state || "",
        city: user.city || "",
        delivery: user.delivery || false,
        bankName: user.bankDetails?.bankName || "",
        accountName: user.bankDetails?.accountName || "",
        accountNumber: user.bankDetails?.accountNumber || "",
        upiId: user.bankDetails?.upiId || "",
        ifscCode: user.bankDetails?.ifscCode || "",
        branchName: user.bankDetails?.branchName || "",
        branchAddress: user.bankDetails?.branchAddress || "",
        openingTime: user.openingTime || "09:00",
        closingTime: user.closingTime || "21:00",
        deliveryTimeMin: user.deliveryTime?.min || 30,
        deliveryTimeMax: user.deliveryTime?.max || 45,
        dineInAvailable: dineInAvailable,
        dineInTotalTables: dineInTotalTables,
        dineInAvailableTables: dineInAvailableTables,
        isWhatsAppNotifications: whatsAppValue,
        orderTypeDineIn: orderTypeDineIn,
        orderTypeTakeaway: orderTypeTakeaway,
        orderTypeDelivery: orderTypeDelivery,
        // ⭐ Delivery Fields
        deliveryCharge: dSettings.deliveryCharge ?? 0,
        freeDeliveryThreshold: dSettings.freeDeliveryThreshold ?? 0,
        minOrderAmountEnabled: dSettings.minOrderAmount?.enabled ?? false,
        minOrderAmount: dSettings.minOrderAmount?.amount ?? 0,
      });

      if (user.profileImage) {
        setProfileImage(user.profileImage);
      } else {
        setProfileImage(null);
      }
    }
  }, [user]);

  // States & Cities
  const allStates = React.useMemo(() => State.getAllStates(), []);

  const citiesOfSelectedState = React.useMemo(() => {
    if (!localProfile.state) return [];
    const stateObj = allStates.find(
      (s) => s.isoCode === localProfile.state || s.name === localProfile.state,
    );
    if (!stateObj) return [];
    return City.getCitiesOfState(stateObj.isoCode);
  }, [localProfile.state, allStates]);

  const selectedStateObj = React.useMemo(() => {
    if (!localProfile.state) return null;
    return (
      allStates.find(
        (s) =>
          s.isoCode === localProfile.state || s.name === localProfile.state,
      ) || null
    );
  }, [allStates, localProfile.state]);

  const selectedCityObj = React.useMemo(() => {
    if (!localProfile.city) return null;
    return (
      citiesOfSelectedState.find(
        (c) => c.name === localProfile.city || c.isoCode === localProfile.city,
      ) || null
    );
  }, [citiesOfSelectedState, localProfile.city]);

  const handleImageChange = (file) => {
    setImageFile(file);
    if (!file) handleRemoveImage();
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;
    const formData = new FormData();
    formData.append("profileImage", imageFile);
    try {
      await dispatch(uploadProfileImage(formData)).unwrap();
    } catch (error) {}
  };

  const handleRemoveImage = async () => {
    try {
      await dispatch(deleteProfileImage()).unwrap();
    } catch (error) {}
  };

  useEffect(() => {
    if (imageFile) {
      handleImageUpload();
    }
  }, [imageFile]);

  const handleProfileChange = (field) => (e) =>
    setLocalProfile((prev) => ({ ...prev, [field]: e.target.value }));

  // ⭐ Synchronized Order Type Toggle (Delivery off hone par auto-sync)
  const handleOrderTypeToggle = (field) => (e) => {
    const checked = e.target.checked;
    setLocalProfile((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  // Profile Save
  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        restaurantName: localProfile.restaurantName,
        ownerName: localProfile.ownerName,
        mobile: localProfile.mobile,
        whatsappNumber: localProfile.whatsappNumber,
        email: localProfile.email,
        restaurantAddress: localProfile.restaurantAddress,
        state: localProfile.state,
        city: localProfile.city,
        delivery:localProfile.delivery,
        openingTime: localProfile.openingTime,
        closingTime: localProfile.closingTime,
        deliveryTime: {
          min: Number(localProfile.deliveryTimeMin),
          max: Number(localProfile.deliveryTimeMax),
        },
        dineIn: {
          isAvailable: Boolean(localProfile.dineInAvailable),
          totalTables: Number(localProfile.dineInTotalTables),
          availableTables: Number(localProfile.dineInAvailableTables),
        },
        bankDetails: {
          bankName: localProfile.bankName,
          accountName: localProfile.accountName,
          accountNumber: localProfile.accountNumber,
          upiId: localProfile.upiId,
          ifscCode: localProfile.ifscCode,
          branchName: localProfile.branchName,
          branchAddress: localProfile.branchAddress,
        },
        isWhatsAppNotifications: localProfile.isWhatsAppNotifications,
        orderType: {
          DINE_IN: localProfile.orderTypeDineIn,
          TAKEAWAY: localProfile.orderTypeTakeaway,
          DELIVERY: localProfile.orderTypeDelivery,
        },
        // ⭐ Delivery Management & Rules Payload
        deliverySettings: {
          isDeliveryAvailable: localProfile.orderTypeDelivery,
          deliveryCharge: Number(localProfile.deliveryCharge || 0),
          freeDeliveryThreshold: Number(
            localProfile.freeDeliveryThreshold || 0,
          ),
          minOrderAmount: {
            enabled: Boolean(localProfile.minOrderAmountEnabled),
            amount: Number(localProfile.minOrderAmount || 0),
          },
        },
      };

      const result = await dispatch(updateProfile(payload)).unwrap();
      if (result) {
        toast.success("Profile & Delivery Settings updated successfully!");
        dispatch(getProfile());
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update profile!");
    }
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData((prev) => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }
    if (!passwordData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your password";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await dispatch(
        updatePassword({
          oldPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        }),
      ).unwrap();
    } catch (error) {
      toast.error("Failed to change password. Please try again.");
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setPaymentFormData((prev) => ({ ...prev, [name]: newValue }));
    dispatch(updateLocalSettings({ [name]: newValue }));
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    dispatch(updatePaymentSettings(paymentFormData));
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector("#restaurant-qr-code svg");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const qrImg = new Image();

    canvas.width = 400;
    canvas.height = 500;

    qrImg.onload = () => {
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, "#0b4064");
      gradient.addColorStop(1, "#0b4064");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, 80);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 80, canvas.width, canvas.height - 80);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 28px 'Arial', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("RestaurantWala Pay", canvas.width / 2, 45);
      ctx.drawImage(qrImg, 75, 120, 250, 250);

      const restaurantName =
        localProfile?.restaurantName || user?.restaurantName || "Restaurant";
      ctx.fillStyle = "#333333";
      ctx.font = "18px 'Arial', sans-serif";
      ctx.fillText("Scan QR Code to Pay", canvas.width / 2, 430);
      ctx.fillStyle = "#666666";
      ctx.font = "16px 'Arial', sans-serif";
      ctx.fillText(`Pay to: ${restaurantName}`, canvas.width / 2, 460);

      const pngFile = canvas.toDataURL("image/png", 1.0);
      const downloadLink = document.createElement("a");
      downloadLink.download = `QR-${restaurantName.replace(/\s+/g, "-")}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    qrImg.src =
      "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));
  };

  const tabs = [
    { label: "Profile", icon: <PersonOutline /> },
    { label: "Restaurant", icon: <Business /> },
    { label: "Payment", icon: <PaymentIcon /> },
    { label: "Password", icon: <LockReset /> },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.8)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-start" },
                gap: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: { xs: "100%", md: 200 },
                }}
              >
                <ProfileImageUpload
                  currentImage={profileImage?.url}
                  onImageChange={handleImageChange}
                  loading={imageUpload.loading || imageDelete.loading}
                  size={140}
                />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#0b4064"
                  sx={{ mt: 1 }}
                >
                  {user?.restaurantName || "Restaurant"}
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ fontWeight: 500 }}
                >
                  {user?.ownerName || ""}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  fontWeight={800}
                  color="#131e32"
                  mb={1}
                >
                  {role} Settings
                </Typography>
                <Typography
                  variant="body1"
                  color="#46639c"
                  sx={{ mb: 3, lineHeight: 1.6, fontWeight: 500 }}
                >
                  Manage your {role} account settings, operational rules, and
                  customer ordering preferences.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Navigation Tabs */}
          <Card
            sx={{
              width: { xs: "100%", md: 280 },
              borderRadius: 3,
              background: "white",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: { xs: "auto", md: "fit-content" },
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={(e, val) => setTabIndex(val)}
              orientation={isMobile ? "horizontal" : "vertical"}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  justifyContent: isMobile ? "center" : "flex-start",
                  alignItems: "center",
                  gap: 2,
                  py: { xs: 1.5, md: 2 },
                  px: 3,
                  color: "#0b4064",
                  "&.Mui-selected": {
                    color: "#0b4064",
                    backgroundColor: "rgba(11, 64, 100, 0.08)",
                    borderRight: isMobile ? "none" : "3px solid #0b4064",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Card>

          {/* Tab Content */}
          <Box sx={{ flex: 1 }}>
            {/* Tab 0: Owner Details */}
            {tabIndex === 0 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  p: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#131e32"
                  mb={1}
                >
                  Owner Details
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ mb: 4, fontWeight: 500 }}
                >
                  Update your personal information and contact details
                </Typography>

                <Box component="form" onSubmit={handleProfileSave}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Owner Name"
                        value={localProfile.ownerName}
                        onChange={handleProfileChange("ownerName")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Restaurant Name"
                        value={localProfile.restaurantName}
                        onChange={handleProfileChange("restaurantName")}
                        fullWidth
                        disabled={!isOwner}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Mobile Number"
                        value={localProfile.mobile}
                        onChange={handleProfileChange("mobile")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="WhatsApp Number"
                        value={localProfile.whatsappNumber}
                        onChange={handleProfileChange("whatsappNumber")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Email Address"
                        value={localProfile.email}
                        onChange={handleProfileChange("email")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Owner Address"
                        value={localProfile.restaurantAddress}
                        onChange={handleProfileChange("restaurantAddress")}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      mt: 4,
                      px: 5,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: "#0b4064",
                      "&:hover": { bgcolor: "#082c44" },
                      textTransform: "none",
                    }}
                    disabled={profileUpdate.loading}
                  >
                    {profileUpdate.loading ? "Saving..." : "Save Owner Details"}
                  </Button>
                </Box>
              </Card>
            )}

            {/* Tab 1: Restaurant Details & Delivery Management */}
            {tabIndex === 1 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  p: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#131e32"
                  mb={1}
                >
                  Restaurant Details & Delivery Rules
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ mb: 4, fontWeight: 500 }}
                >
                  Update your restaurant operations, delivery charges, and order
                  types
                </Typography>

                <Box component="form" onSubmit={handleProfileSave}>
                  {/* Location */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="#131e32"
                    sx={{ mb: 2 }}
                  >
                    Location
                  </Typography>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        options={allStates}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedStateObj}
                        onChange={(_, newValue) => {
                          setLocalProfile((prev) => ({
                            ...prev,
                            state: newValue ? newValue.isoCode : "",
                            city: "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="State" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        options={citiesOfSelectedState}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedCityObj}
                        onChange={(_, newValue) => {
                          setLocalProfile((prev) => ({
                            ...prev,
                            city: newValue ? newValue.name : "",
                          }));
                        }}
                        disabled={!localProfile.state}
                        renderInput={(params) => (
                          <TextField {...params} label="City" fullWidth />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Hours */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="#131e32"
                    sx={{ mb: 2 }}
                  >
                    Restaurant Hours
                  </Typography>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Opening Time"
                        type="time"
                        value={localProfile.openingTime}
                        onChange={handleProfileChange("openingTime")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Closing Time"
                        type="time"
                        value={localProfile.closingTime}
                        onChange={handleProfileChange("closingTime")}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>

                  {/* Order Types Toggle */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="#131e32"
                    sx={{ mb: 1 }}
                  >
                    Order Types
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 3 }}
                  >
                    Enable or disable order types available for customers
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Dine In */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: "2px solid",
                          borderColor: localProfile.orderTypeDineIn
                            ? "#0b4064"
                            : "grey.200",
                          borderRadius: 2,
                          bgcolor: localProfile.orderTypeDineIn
                            ? "rgba(11, 64, 100, 0.04)"
                            : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <LocalDiningIcon
                              sx={{
                                color: localProfile.orderTypeDineIn
                                  ? "#0b4064"
                                  : "#999",
                              }}
                            />
                            <Typography variant="subtitle2" fontWeight={700}>
                              Dine In
                            </Typography>
                          </Box>
                          <Switch
                            checked={localProfile.orderTypeDineIn}
                            onChange={handleOrderTypeToggle("orderTypeDineIn")}
                          />
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Takeaway */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: "2px solid",
                          borderColor: localProfile.orderTypeTakeaway
                            ? "#0b4064"
                            : "grey.200",
                          borderRadius: 2,
                          bgcolor: localProfile.orderTypeTakeaway
                            ? "rgba(11, 64, 100, 0.04)"
                            : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <LunchDiningIcon
                              sx={{
                                color: localProfile.orderTypeTakeaway
                                  ? "#0b4064"
                                  : "#999",
                              }}
                            />
                            <Typography variant="subtitle2" fontWeight={700}>
                              Takeaway
                            </Typography>
                          </Box>
                          <Switch
                            checked={localProfile.orderTypeTakeaway}
                            onChange={handleOrderTypeToggle(
                              "orderTypeTakeaway",
                            )}
                          />
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Delivery Toggle */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: "2px solid",
                          borderColor: localProfile.orderTypeDelivery
                            ? "#0b4064"
                            : "grey.200",
                          borderRadius: 2,
                          bgcolor: localProfile.orderTypeDelivery
                            ? "rgba(11, 64, 100, 0.04)"
                            : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <DeliveryDiningIcon
                              sx={{
                                color: localProfile.orderTypeDelivery
                                  ? "#0b4064"
                                  : "#999",
                              }}
                            />
                            <Typography variant="subtitle2" fontWeight={700}>
                              Delivery
                            </Typography>
                          </Box>
                          <Switch
                            checked={localProfile.orderTypeDelivery}
                            onChange={handleOrderTypeToggle(
                              "orderTypeDelivery",
                            )}
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                  {/* Dine In Details */}
                  {localProfile.orderTypeDineIn && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color="#131e32"
                        sx={{ mb: 2 }}
                      >
                        Dine-in Settings
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Total Tables"
                            type="number"
                            value={localProfile.dineInTotalTables}
                            onChange={handleProfileChange("dineInTotalTables")}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Available Tables"
                            type="number"
                            value={localProfile.dineInAvailableTables}
                            onChange={handleProfileChange(
                              "dineInAvailableTables",
                            )}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* ⭐ DELIVERY MANAGEMENT & RULES (Only Visible When Delivery is Enabled) */}
                  {localProfile.orderTypeDelivery && (
                    <Box
                      sx={{
                        p: 3,
                        mb: 4,
                        borderRadius: 3,
                        bgcolor: "#fffaf5",
                        border: "1.5px solid #FF6B0030",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <TwoWheelerIcon
                          sx={{ color: "#FF6B00", fontSize: 26 }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color="#131e32"
                        >
                          Delivery Charges & Rules
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: "block", mb: 2.5 }}
                      >
                        Configure delivery charges, free delivery limits, and
                        minimum order rules.
                      </Typography>

                      <Grid container spacing={3}>
                        {/* Delivery Charge */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            label="Standard Delivery Charge (₹)"
                            type="number"
                            value={localProfile.deliveryCharge}
                            onChange={handleProfileChange("deliveryCharge")}
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText="Flat fee for delivery orders (0 = Free Delivery)"
                          />
                        </Grid>

                        {/* Free Delivery Threshold */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            label="Free Delivery Above (₹)"
                            type="number"
                            value={localProfile.freeDeliveryThreshold}
                            onChange={handleProfileChange(
                              "freeDeliveryThreshold",
                            )}
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText="Order amount to get Free Delivery (0 = Always charge)"
                          />
                        </Grid>

                        {/* Estimated Time */}
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                              label="Min Time (Mins)"
                              type="number"
                              value={localProfile.deliveryTimeMin}
                              onChange={handleProfileChange("deliveryTimeMin")}
                              fullWidth
                            />
                            <TextField
                              label="Max Time (Mins)"
                              type="number"
                              value={localProfile.deliveryTimeMax}
                              onChange={handleProfileChange("deliveryTimeMax")}
                              fullWidth
                            />
                          </Box>
                        </Grid>

                        {/* Minimum Order Amount Toggle & Field */}
                        <Grid size={{ xs: 12 }}>
                          <Divider sx={{ my: 1 }} />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                Enable Minimum Order Amount for Delivery
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Customers cannot place a delivery order below
                                this cart value.
                              </Typography>
                            </Box>
                            <Switch
                              checked={localProfile.minOrderAmountEnabled}
                              onChange={(e) =>
                                setLocalProfile((prev) => ({
                                  ...prev,
                                  minOrderAmountEnabled: e.target.checked,
                                }))
                              }
                            />
                          </Box>

                          {localProfile.minOrderAmountEnabled && (
                            <Box sx={{ mt: 2, maxWidth: 300 }}>
                              <TextField
                                label="Minimum Order Amount (₹)"
                                type="number"
                                value={localProfile.minOrderAmount}
                                onChange={handleProfileChange("minOrderAmount")}
                                fullWidth
                                size="small"
                                InputProps={{ inputProps: { min: 1 } }}
                                placeholder="e.g. 199"
                              />
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* delivery boy settings */}
                  <Card sx={{ mt: 3, p: 2, mb:3, border: "1px solid", borderColor: localProfile.delivery ? "primary.main" : "divider" }}>
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Box>
      <Typography variant="subtitle1" fontWeight="bold">
        City Delivery Partner Service
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {localProfile.delivery
          ? "Enabled: Delivery boys of your city can see this restaurant and deliver orders."
          : "Disabled: Your restaurant and orders will remain hidden from delivery boys."}
      </Typography>
    </Box>
    <Switch
      checked={Boolean(localProfile.delivery)}
      onChange={(e) =>
        setLocalProfile((prev) => ({
          ...prev,
          delivery: e.target.checked,
        }))
      }
      color="primary"
    />
  </Box>
</Card>

                  {/* WhatsApp Notifications */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="#131e32"
                    sx={{ mb: 2 }}
                  >
                    Notification Settings
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 4,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        WhatsApp Notifications
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Send order updates and confirmations to your WhatsApp
                      </Typography>
                    </Box>
                    <Switch
                      checked={localProfile.isWhatsAppNotifications}
                      onChange={(e) =>
                        setLocalProfile((prev) => ({
                          ...prev,
                          isWhatsAppNotifications: e.target.checked,
                        }))
                      }
                    />
                  </Paper>

                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: "#0b4064",
                      "&:hover": { bgcolor: "#082c44" },
                      textTransform: "none",
                    }}
                    disabled={profileUpdate.loading}
                  >
                    {profileUpdate.loading
                      ? "Saving..."
                      : "Save Restaurant Details"}
                  </Button>
                </Box>
              </Card>
            )}

            {/* Tab 2: Payment Settings */}
            {tabIndex === 2 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  p: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#131e32"
                  mb={1}
                >
                  Payment Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ mb: 4, fontWeight: 500 }}
                >
                  Configure your payment methods and preferences
                </Typography>

                {paymentSettingsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handlePaymentSubmit}>
                    {/* Cash */}
                    <Card sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="h6">Cash Payment</Typography>
                        <Switch
                          name="cashEnabled"
                          checked={paymentFormData.cashEnabled}
                          onChange={handlePaymentChange}
                        />
                      </CardContent>
                    </Card>

                    {/* UPI */}
                    <Card sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">UPI Payment</Typography>
                          <Switch
                            name="upiEnabled"
                            checked={paymentFormData.upiEnabled}
                            onChange={handlePaymentChange}
                          />
                        </Box>
                        {paymentFormData.upiEnabled && (
                          <TextField
                            fullWidth
                            label="UPI ID"
                            name="upiId"
                            value={paymentFormData.upiId}
                            onChange={handlePaymentChange}
                            placeholder="restaurant@upi"
                          />
                        )}
                      </CardContent>
                    </Card>

                    {/* PhonePe */}
                    <Card sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">PhonePe Payment</Typography>
                          <Switch
                            name="phonePeEnabled"
                            checked={paymentFormData.phonePeEnabled}
                            onChange={handlePaymentChange}
                          />
                        </Box>
                        {paymentFormData.phonePeEnabled && (
                          <TextField
                            fullWidth
                            label="PhonePe Number"
                            name="phonePeNumber"
                            value={paymentFormData.phonePeNumber}
                            onChange={handlePaymentChange}
                            placeholder="9876543210"
                          />
                        )}
                      </CardContent>
                    </Card>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={paymentOperationLoading}
                      sx={{
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        bgcolor: "#0b4064",
                        "&:hover": { bgcolor: "#082c44" },
                        textTransform: "none",
                      }}
                    >
                      {paymentOperationLoading
                        ? "Saving..."
                        : "Save Payment Settings"}
                    </Button>
                  </Box>
                )}
              </Card>
            )}

            {/* Tab 3: Password */}
            {tabIndex === 3 && (
              <Card
                sx={{
                  borderRadius: 3,
                  background: "white",
                  p: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#131e32"
                  mb={1}
                >
                  Change Password
                </Typography>
                <Typography
                  variant="body2"
                  color="#0b4064"
                  sx={{ mb: 4, fontWeight: 500 }}
                >
                  Update your password to keep your account secure
                </Typography>

                <Box component="form" onSubmit={handlePasswordSave}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Current Password"
                        type={showPassword.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange("currentPassword")}
                        fullWidth
                        error={!!formErrors.currentPassword}
                        helperText={formErrors.currentPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  handleClickShowPassword("current")
                                }
                              >
                                {showPassword.current ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="New Password"
                        type={showPassword.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange("newPassword")}
                        fullWidth
                        error={!!formErrors.newPassword}
                        helperText={formErrors.newPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => handleClickShowPassword("new")}
                              >
                                {showPassword.new ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Confirm New Password"
                        type={showPassword.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange("confirmPassword")}
                        fullWidth
                        error={!!formErrors.confirmPassword}
                        helperText={formErrors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  handleClickShowPassword("confirm")
                                }
                              >
                                {showPassword.confirm ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={passwordUpdate.loading}
                    sx={{
                      mt: 4,
                      px: 5,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: "#0b4064",
                      "&:hover": { bgcolor: "#082c44" },
                      textTransform: "none",
                    }}
                  >
                    {passwordUpdate.loading ? "Updating..." : "Change Password"}
                  </Button>
                </Box>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
